/**
 * Overlay API
 *
 * Typed client functions for the overlay engine admin API (/api/overlays).
 * Mirrors the backend route shapes in pratbot/api/routes/overlays.py.
 *
 * Auth: these endpoints require a logged-in session; the shared api client
 * attaches the JWT automatically. Channel access is enforced server-side.
 */

import api from '@/api/client'

// --- Types (mirror the backend Pydantic models) ---------------------------

export type OverlaySource =
  | 'system_default'
  | 'user_override'
  | 'custom_structured'
  | 'custom_html'

export type OverlayType = 'alert' | 'media' | 'goal_bar' | 'event_list' | 'label'

/** One author-declared, streamer-tweakable setting (custom_html overlays). */
export interface FieldSpec {
  key: string
  type: 'text' | 'number' | 'color' | 'boolean'
  label?: string
  default?: unknown
}

export interface Overlay {
  id: string
  channel_id: number | null
  overlay_type: OverlayType
  source: OverlaySource
  slot: string
  parent_id: string | null
  name: string
  has_token: boolean
  config: Record<string, unknown>
  capability_bindings: Record<string, unknown>
  enabled: boolean
  /** custom_html overlays — author bodies + declared settings (null/[] otherwise). */
  html_body?: string | null
  css_body?: string | null
  js_body?: string | null
  fields_schema?: FieldSpec[]
}

export interface OverlayWithToken {
  overlay: Overlay
  /** Plaintext token — shown ONCE, not recoverable. */
  token: string
  /** Full URL to paste into OBS. */
  overlay_url: string
}

export interface AlertVariant {
  id: number
  overlay_id: string
  alert_type: string
  variant: string
  condition: Record<string, unknown>
  asset_image_id: number | null
  asset_sound_id: number | null
  template: string
  duration_ms: number
  style: Record<string, unknown>
}

export interface OverlayUpdate {
  name?: string
  config?: Record<string, unknown>
  capability_bindings?: Record<string, unknown>
  enabled?: boolean
  html_body?: string
  css_body?: string
  js_body?: string
  fields_schema?: FieldSpec[]
}

export interface CustomOverlayCreate {
  channel_id: number
  slot: string
  name?: string
  overlay_type?: OverlayType
  html_body?: string
  css_body?: string
  js_body?: string
  fields_schema?: FieldSpec[]
  capability_bindings?: Record<string, unknown>
  config?: Record<string, unknown>
}

export interface VariantCreate {
  alert_type: string
  variant?: string
  condition?: Record<string, unknown>
  asset_image_id?: number | null
  asset_sound_id?: number | null
  template?: string
  duration_ms?: number
  style?: Record<string, unknown>
}

export type VariantUpdate = Partial<Omit<VariantCreate, 'alert_type'>> & {
  alert_type?: string
}

export interface TestFireResult {
  status: 'fired' | 'no_render'
  overlay_id?: string
  type?: string
  test?: boolean
  detail?: string
}

// --- API functions ---------------------------------------------------------

/** List a channel's overlays plus adoptable system defaults. */
export function listOverlays(channelId: number): Promise<Overlay[]> {
  return api.get<Overlay[]>(`/api/overlays?channel_id=${channelId}`)
}

/** Get one overlay (never includes the token). */
export function getOverlay(overlayId: string): Promise<Overlay> {
  return api.get<Overlay>(`/api/overlays/${overlayId}`)
}

/** Adopt a system default into a channel; returns the one-time token + URL. */
export function adoptOverlay(
  channelId: number,
  slot: string,
  name?: string,
): Promise<OverlayWithToken> {
  return api.post<OverlayWithToken>('/api/overlays/adopt', {
    channel_id: channelId,
    slot,
    name,
  })
}

/** Create a bespoke custom_html overlay (no token; mint via regenerateToken). */
export function createCustomOverlay(
  data: CustomOverlayCreate,
): Promise<Overlay> {
  return api.post<Overlay>('/api/overlays/custom', data)
}

/** Mint a fresh token (invalidates the old URL); returns it once. */
export function regenerateToken(overlayId: string): Promise<OverlayWithToken> {
  return api.post<OverlayWithToken>(`/api/overlays/${overlayId}/regenerate-token`)
}

/** Edit an overlay's presentation fields (partial). */
export function updateOverlay(
  overlayId: string,
  patch: OverlayUpdate,
): Promise<Overlay> {
  return api.patch<Overlay>(`/api/overlays/${overlayId}`, patch)
}

/** List an overlay's alert variants. */
export function listVariants(overlayId: string): Promise<AlertVariant[]> {
  return api.get<AlertVariant[]>(`/api/overlays/${overlayId}/variants`)
}

/** Create an alert variant. */
export function createVariant(
  overlayId: string,
  data: VariantCreate,
): Promise<AlertVariant> {
  return api.post<AlertVariant>(`/api/overlays/${overlayId}/variants`, data)
}

/** Update an alert variant (partial). */
export function updateVariant(
  overlayId: string,
  variantId: number,
  patch: VariantUpdate,
): Promise<AlertVariant> {
  return api.patch<AlertVariant>(
    `/api/overlays/${overlayId}/variants/${variantId}`,
    patch,
  )
}

/** Delete an alert variant. */
export function deleteVariant(
  overlayId: string,
  variantId: number,
): Promise<void> {
  return api.delete<void>(`/api/overlays/${overlayId}/variants/${variantId}`)
}

/** Fire a synthetic event through the real pipeline (preview == live). */
export function testFire(
  overlayId: string,
  capability: string,
  fields: Record<string, unknown>,
): Promise<TestFireResult> {
  return api.post<TestFireResult>(`/api/overlays/${overlayId}/test-fire`, {
    capability,
    fields,
  })
}

/** Build the OBS overlay URL for display (token only known at adopt/regen time). */
export function overlayPagePath(overlayId: string): string {
  return `/overlay/${overlayId}`
}
